import React, {createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Col, DatePicker, Form, Input, Modal, Row, Select, Spin, TimePicker} from 'antd';
import dayjs, {Dayjs} from 'dayjs';
import {
    CalendarEventResponse,
    MedicalStaff,
    ClinicResponse,
    PatientListResponse,
    SyncStatus,
    CalendarEventCreate,
    CalendarEventUpdate
} from "../../api-client";
import {WashEvent} from "../../types";
import {getCalendarApi, getPatientApi} from "../../api/apiInstances";
import {
    mapCalendarEventsToWashEvents,
    mapCalendarEventToWashEvent,
    mapWashEventToCalendarEvent,
    mapWashEventToCalendarEventCreate,
    deleteCalendarEvent,
    dateToLocalISO
} from "../../utils/calendarUtils";
import {FormattedMessage, useIntl} from "react-intl";
import {AlertContext} from "../../context/AlertContext";
import {CaretDownOutlined, CloseOutlined} from "@ant-design/icons";
import {isMobileOnly} from "react-device-detect";
import type {SelectProps} from 'antd';
import '../../assets/css/EventModalWrapper.css';
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import logger from "../../utils/logger";
import {ALERT_DURATIONS} from "../../constants/alerts";
import {useTrackEvent} from "../../hooks/useTrackEvent";

export interface User {
    id: number;
    name: string;
}

export interface UserContextType {
    user: User;
    setUser: (user: User) => void;
}

export const UserContext = createContext<UserContextType>({
    user: {name: 'Admin', id: 0},
    setUser: (_user: User) => {
    },
});

interface EventModalWrapperProps {
    isOpen: boolean;
    onClose: () => void;
    selectedEvent: WashEvent;
    doctors: MedicalStaff[];
    clinics: ClinicResponse[];
    onDoctorChange: (value: string) => void;
    onRoomChange: (value: string) => void;
    form: any;
    appointments: WashEvent[];
    setAppointments: Dispatch<SetStateAction<WashEvent[]>>;
    setAllAppointments: Dispatch<SetStateAction<WashEvent[]>>;
    setSelectedEvent: Dispatch<SetStateAction<WashEvent | null>>;
}

const EventModalWrapper: React.FC<EventModalWrapperProps> = ({
                                                                 isOpen,
                                                                 selectedEvent,
                                                                 onClose,
                                                                 clinics,
                                                                 form,
                                                                 onRoomChange,
                                                                 onDoctorChange,
                                                                 appointments,
                                                                 setAppointments,
                                                                 setAllAppointments,
                                                                 setSelectedEvent,
                                                                 doctors,
                                                             }) => {
    const intl = useIntl();
    const {principal} = React.useContext(DentobookApplicationContext);
    const track = useTrackEvent();
    const [endTimeError, setEndTimeError] = useState<string | null>(null);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [patients, setPatients] = useState<PatientListResponse[]>([]);
    const keyword: string = '';
    const sortBy: 'id' | 'name' | 'lastHour' | 'nextHour' = 'id';
    const order: 'asc' | 'desc' = 'asc';
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs(selectedEvent.start));
    const {Option} = Select;
    const {user} = useContext(UserContext);
    const hasEditAccess = user.id === selectedEvent.user;
    const [initialDoctorSet, setInitialDoctorSet] = useState(false);
    const [startValue, setStartValue] = useState<Dayjs>(dayjs(selectedEvent.start));
    const [endValue, setEndValue] = useState<Dayjs>(dayjs(selectedEvent.end));
    const [selectedPatient, setSelectedPatient] = useState<any>(selectedEvent.patient || '');
    const [selectedUnregisteredPatient, setSelectedUnregisteredPatient] = useState<string>(selectedEvent.patient || '');
    const [selectedDoctor, setSelectedDoctor] = useState<string>(selectedEvent.doctor || '');
    const [selectedRoom, setSelectedRoom] = useState<string>(selectedEvent.clinicId || '');
    const isClinicDeleted = !!selectedRoom && !clinics.some(c => c.id === selectedRoom);
    const [selectedNotes, setSelectedNotes] = useState<string>(selectedEvent.notes ?? '');
    const [deleteGoogleEvent, setDeleteGoogleEvent] = useState<string>(selectedEvent.googleId || '');
    const patientApi = getPatientApi();
    const calendarApi = getCalendarApi();
    const {setVisible, setError, setWarning } = useContext(AlertContext);
    const [loading, setLoading] = useState(true);
    const formKey = useMemo(() => selectedEvent?.id || 'new-event', [selectedEvent]);
    const [selectValue, setSelectValue] = useState(() => {
        let initialSelectValue = [];
        if (selectedPatient) {
            initialSelectValue.push(selectedPatient);
        } else if (selectedUnregisteredPatient) {
            initialSelectValue.push(selectedUnregisteredPatient);
        }
        return initialSelectValue;
    });
    const selectRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setStartValue(dayjs(selectedEvent.start));
        setEndValue(dayjs(selectedEvent.end));
    }, [selectedEvent]);

    useEffect(() => {
        if (appointments.length === 0) {
            loadEvents();
        }
        if (patients.length === 0) {
            loadData();
        }
    }, []);

    useEffect(() => {
        let patientValue: any[] = [];

        if (selectedPatient) {
            patientValue = [selectedPatient];
        } else if (selectedUnregisteredPatient) {
            patientValue = [selectedUnregisteredPatient];
        }
        if (selectedEvent) {
            form.setFieldsValue({
                patient: patientValue,
                doctor: selectedEvent.doctor,
                clinic: selectedEvent.clinicId,
                notes: selectedEvent.notes,

            });
        } else {
            form.resetFields();
        }
    }, [selectedEvent, form]);

    useEffect(() => {
        let patientValue: any[] = [];

        if (selectedPatient) {
            patientValue = [selectedPatient];
        } else if (selectedUnregisteredPatient) {
            patientValue = [selectedUnregisteredPatient];
        }
        if (selectedEvent) {
            form.setFieldsValue({
                patient: patientValue,
                doctor: selectedEvent.doctor,
                clinic: selectedEvent.clinicId,
                notes: selectedEvent.notes,
            });
        } else {
            form.resetFields();
        }
    }, [selectedPatient, selectedUnregisteredPatient]);

    useEffect(() => {
        if (selectedPatient) {
            setSelectValue([selectedPatient]);
        } else if (selectedUnregisteredPatient) {
            setSelectValue([selectedUnregisteredPatient]);
        }
    }, [selectedPatient, selectedUnregisteredPatient]);

    // Auto-select the clinic when there is exactly one available and none is pre-selected
    useEffect(() => {
        if (clinics.length === 1 && !selectedRoom && clinics[0].id) {
            const singleId = clinics[0].id;
            setSelectedRoom(singleId);
            form.setFieldsValue({clinic: singleId});
            setSelectedEvent(prev => prev ? {...prev, clinicId: singleId} : null);
        }
    }, [clinics, selectedEvent?.id]);


    const loadData = () => {
        let allPatients: PatientListResponse[] = [];

        const fetchPage = (page: number): Promise<PatientListResponse[]> => {
            return patientApi.fetchAllPatients(keyword, sortBy, order, page).then((response) => {

                if (typeof response.data.pagesCount === 'undefined'
                    || typeof response.data.patients === 'undefined'
                    || typeof response.data.totalElements === 'undefined') {
                    return allPatients;
                }

                allPatients = [...allPatients, ...response.data.patients];

                if (page + 1 < response.data.pagesCount) {
                    return fetchPage(page + 1);
                } else {
                    return allPatients;
                }
            });
        }

        fetchPage(0).then(patients => {
            setPatients(patients);
            setLoading(false);
        });
    }


    const loadEvents = () => {
        calendarApi.fetchAllCalendarEvents().then((response) => {
            return setAppointments(mapCalendarEventsToWashEvents(response.data))
        });
    }

    useEffect(() => {
        setStartValue(dayjs(selectedEvent.start));
        setEndValue(dayjs(selectedEvent.end));
    }, [selectedEvent]);

    const options: SelectProps['options'] = useMemo(() => {
        return patients.map((patient) => ({
            value: patient.id,
            label: `${patient.firstName} ${patient.lastName ?? ''}`.trim(),
        }));
    }, [patients]);

    const doctorOptions: SelectProps['options'] = doctors
        .filter(doctor => doctor.role === 'MEDICAL_DOCTOR' || doctor.role === 'CLINIC_ADMIN')
        .map(doctor => ({
            value: doctor.id,
            label: `${doctor.firstName} ${doctor.lastName ?? ''}`,
        }));

    useEffect(() => {
        if (initialDoctorSet || selectedEvent.id || principal?.user?.role !== 'MEDICAL_DOCTOR') return;
        const myStaff = doctors.find(doc => doc.id === principal?.user?.medicalStaffId)
            ?? doctors.find(doc => doc.userId === principal?.user?.id);
        if (myStaff) {
            form.setFieldsValue({ doctor: myStaff.id });
            setSelectedDoctor(myStaff.id ?? '');
            setInitialDoctorSet(true);
        }
    }, [doctors, principal, selectedEvent.id, initialDoctorSet]);


    const onNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setSelectedNotes(event.target.value);
    };

    const handleDateChange = (date: Dayjs | null) => {
        if (date) {
            const newStartDate: Dayjs = dayjs(selectedEvent.start).year(date.year()).month(date.month()).date(date.date());
            const newEndDate: Dayjs = dayjs(selectedEvent.end).year(date.year()).month(date.month()).date(date.date());

            const updatedEvent: WashEvent = {
                ...selectedEvent,
                start: newStartDate.toDate(),
                end: newEndDate.toDate(),
            };

            setSelectedEvent(updatedEvent);
            setSelectedDate(date);

        }
    };

    const onDeleteButtonClick = async (e?: React.MouseEvent<HTMLButtonElement>) => {
        try {
            let googleEventId: string | undefined = deleteGoogleEvent;

            if (!deleteGoogleEvent) {
                try {
                    const response = await calendarApi.fetchCalendarEventById(selectedEvent.id);
                    if(response.data.googleId){
                        googleEventId = response.data.googleId;
                    }
                } catch (error: any) {
                    setError(error.message || "No Google Event ID found for the selected event.");
                    setVisible(true);
                    setTimeout(() => {
                        setVisible(false);
                        setError('');
                    }, ALERT_DURATIONS.STANDARD);
                }
            }

            if (googleEventId) {
                const {error} = await deleteGoogleCalendarEvent(googleEventId);
                if (error) {
                    selectedEvent.syncStatus = SyncStatus.DeletePending;
                } else {
                    selectedEvent.syncStatus = SyncStatus.Synced;
                }
            }

            // Delete the event in your local API
            deleteCalendarEvent(
                selectedEvent.id,
                calendarApi
            );

            // Update local appointments list
            const updatedAppointments = appointments.filter((appointment) => appointment.id !== selectedEvent.id);
            setAppointments(updatedAppointments);

        } catch (error: any) {
            setError(error.message || "An unexpected error occurred");
            setVisible(true);
            setTimeout(() => {
                setVisible(false);
                setError('');
            }, ALERT_DURATIONS.STANDARD);
        } finally {
            // Clean-up
            setDeleteGoogleEvent('');
            onClose();
        }
    };


    const showDeleteModal = (appointmentId: string, appointmentGoogleId: string) => {
        setDeleteGoogleEvent(appointmentGoogleId);
        setIsDeleteModalVisible(true);
    };

    const handleDeleteConditionCancel = () => {
        setDeleteGoogleEvent('')
        setIsDeleteModalVisible(false);
    };

    const onStartChange = (time: any) => {
        if (time) {
            setStartValue(time);
            selectedEvent.start = time.toDate();
        }
    };

    const onEndChange = (time: any) => {
        if (time) {
            if (time.toDate().getHours() === 19 && time.toDate().getMinutes() > 0) {
                time = dayjs().hour(19).minute(0);
            }
            setEndValue(time);
            selectedEvent.end = time.toDate();
        }
    };

    const closeModal = () => {
        onClose();
        setSelectedEvent(null);
        setSelectedPatient('');
        setSelectedUnregisteredPatient('')
        setSelectedDoctor('');
        setSelectedRoom('');
    };

    const handleError = (error: any) => {
        // Check for Python backend error format (FastAPI returns "detail")
        const errorMessage = error.response?.data?.detail || error.response?.data?.message;

        if (errorMessage) {
            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);

            setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
            setTimeout(() => {
                setError('');
            }, ALERT_DURATIONS.STANDARD);

        } else {
            logger.log('error msg', error);
            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            setError("Failed to create/update calendar event:");
            setTimeout(() => {
                setError('');
            }, ALERT_DURATIONS.STANDARD);
        }
    }

    const showSuccessAlert = () => {
        setVisible(true);
        setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
    }

    // NOTE: Google Calendar V1 manual sync functions removed.
    // V2 integration handles sync automatically on backend when events are created/updated/deleted.
    const createGoogleCalendarEvent = async (event: CalendarEventResponse): Promise<{
        event?: CalendarEventResponse,
        error?: string
    }> => {
        // Google Calendar sync now happens automatically on backend (V2)
        return {event};
    };

    const updateGoogleCalendarEvent = async (event: CalendarEventResponse): Promise<{
        event?: CalendarEventResponse,
        error?: string
    }> => {
        // Google Calendar sync now happens automatically on backend (V2)
        return {event};
    };

    const deleteGoogleCalendarEvent = async (eventId: string): Promise<{ success?: boolean, error?: string }> => {
        // Google Calendar sync now happens automatically on backend (V2)
        return {success: true};
    };

    // ============ DTO BUILDERS ============
    const buildCreateDto = (washEvent: WashEvent, organizationId: string): CalendarEventCreate => {
        return mapWashEventToCalendarEventCreate(washEvent, organizationId);
    };

    const buildUpdateDto = (washEvent: WashEvent): CalendarEventUpdate => {
        return mapWashEventToCalendarEvent(washEvent);
    };

    // ============ SYSTEM PERSISTENCE HELPERS ============
    const createInSystem = async (dto: CalendarEventCreate, syncStatus: SyncStatus): Promise<CalendarEventResponse> => {
        const createRequest: CalendarEventCreate = { ...dto, syncStatus };
        const result = await calendarApi.createCalendarEvent(createRequest);
        return result.data;
    };

    const updateInSystem = async (eventId: string, dto: CalendarEventUpdate, syncStatus: SyncStatus): Promise<CalendarEventResponse> => {
        const updateRequest: CalendarEventUpdate = { ...dto, syncStatus };
        const result = await calendarApi.updateCalendarEvent(eventId, updateRequest);
        return result.data;
    };

    const handleCalendarEvent = async (updatedEvent: WashEvent): Promise<void> => {
        // Step 1: Determine create vs update
        const isCreate = !selectedEvent?.id;
        const eventId = selectedEvent?.id;
        const organizationId = principal?.user?.organizationId;

        // Step 2: Validate organizationId required for create
        if (isCreate && !organizationId) {
            Modal.error({ title: 'Error', content: 'Missing organization ID' });
            throw new Error("Missing organization ID");
        }

        // Step 3: Build DTO
        const dto = isCreate
            ? buildCreateDto(updatedEvent, organizationId!)
            : buildUpdateDto(updatedEvent);

        // Step 4: Determine patientEmail (only if dto has patient id)
        let patientEmail: string | undefined;
        if (dto.patient) {
            try {
                const response = await patientApi.fetchPatientById(dto.patient);
                patientEmail = response.data.email || undefined;
            } catch (err) {
                logger.log(err);
            }
        }

        // Step 5: Handle no patient email case
        if (!patientEmail) {
            setWarning(intl.formatMessage({id: 'patientEmailAlert'}));
            setVisible(true);
            setTimeout(() => {
                setVisible(false);
                setWarning('');
            }, ALERT_DURATIONS.STANDARD);

            try {
                if (isCreate) {
                    const response = await createInSystem(dto as CalendarEventCreate, SyncStatus.CreatePending);
                    updatedEvent.id = response.id;
                    handleAppointmentsUpdate(updatedEvent);
                    setAllAppointments(prevAppointments => [...prevAppointments, mapCalendarEventToWashEvent(response)]);
                    track('appointment_created');
                } else {
                    if (!eventId) {
                        throw new Error("Missing event ID for update");
                    }
                    await updateInSystem(eventId, dto as CalendarEventUpdate, SyncStatus.UpdatePending);
                    handleAppointmentsUpdate(updatedEvent);
                }
            } catch (error) {
                handleError(error);
            }
            return;
        }

        // Step 6: Create/update event (Google Calendar sync happens automatically on backend - V2)
        try {
            if (isCreate) {
                const createDto = dto as CalendarEventCreate;
                const response = await createInSystem(createDto, SyncStatus.Synced);
                updatedEvent.id = response.id;
                updatedEvent.syncStatus = SyncStatus.Synced;
                handleAppointmentsUpdate(updatedEvent);
                setAllAppointments(prevAppointments => [...prevAppointments, mapCalendarEventToWashEvent(response)]);
                track('appointment_created');
            } else {
                // Update path
                if (!eventId) {
                    throw new Error("Missing event ID for update");
                }

                const updateDto = dto as CalendarEventUpdate;
                await updateInSystem(eventId, updateDto, SyncStatus.Synced);
                updatedEvent.syncStatus = SyncStatus.Synced;
                handleAppointmentsUpdate(updatedEvent);
            }
        } catch (error) {
            handleError(error);
        }
    };


    const handleAppointmentsUpdate = (updatedEvent: WashEvent) => {
        const existingAppointmentIndex = appointments.findIndex((appointment) => appointment.id === updatedEvent.id);

        const updatedAppointments = [...appointments];
        if (existingAppointmentIndex !== -1) {
            updatedAppointments[existingAppointmentIndex] = updatedEvent;
        } else {
            updatedAppointments.push(updatedEvent);
        }
        setAppointments(updatedAppointments);
    };

    const onSaveButtonClick = async () => {
        try {
            if (startValue && endValue && endValue.isBefore(startValue)) {
                setEndTimeError(intl.formatMessage({id: "endTime_error"}));

                return;
            }
            await form.validateFields();

            const doctor = doctors.find(doc => doc.id === selectedDoctor);
            const doctorName = doctor ? `${doctor.firstName} ${doctor.lastName}` : "";

            let patientName: string;
            let effectivePatientValue = "";

            if (selectedUnregisteredPatient) {
                effectivePatientValue = selectedUnregisteredPatient;
                setSelectedPatient('');
            } else if (selectedPatient) {
                effectivePatientValue = selectedPatient;
            }
            const patient = patients.find(pat => pat.id === selectedPatient);

            if (patient) {
                const firstName = patient.firstName ?? "";
                const lastName = patient.lastName ? ` ${patient.lastName}` : "";
                patientName = `${firstName}${lastName}`.trim();
            } else {
                patientName = selectedPatient || selectedUnregisteredPatient;
            }
            const eventTitle = `${patientName} \n ${doctorName}`;
            const updatedEvent: WashEvent = {
                ...selectedEvent,
                googleId: selectedEvent.googleId,
                title: eventTitle,
                start: startValue.toDate(),
                end: endValue.toDate(),
                doctor: selectedDoctor,
                clinicId: selectedRoom,
                patient: effectivePatientValue,
                notes: selectedNotes,
            };

            handleCalendarEvent(updatedEvent)
                .then(() => {
                    onClose();
                    showSuccessAlert();
                })
                .catch(error => {
                    logger.error("Failed to save calendar event:", error);
                });
        } catch (errorInfo) {
            logger.log('Failed:', errorInfo);
            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            const errorMessage = intl.formatMessage({
                id: 'fill_all_required_fields',
                defaultMessage: 'Попълнете всички задължителни полета.'
            });

            setError(errorMessage);
            setTimeout(() => {
                setError('');
            }, ALERT_DURATIONS.STANDARD);
        }

    };
    const footer = <>

        {hasEditAccess && selectedEvent?.id && !isMobileOnly && (
            <Button
                className="calendar-form-btn-delete dentobook-btn-large color-error"
                danger
                onClick={() => showDeleteModal(selectedEvent.id, selectedEvent.googleId)}
            >
                <FormattedMessage id="delete"/>
            </Button>
        )}

        <Button className="calendar-form-btn-secondary" onClick={() => closeModal()}>
            {<FormattedMessage id="cancel"/>}
        </Button>
        {hasEditAccess && (
            <Button className="calendar-form-btn-primary" type="primary" onClick={onSaveButtonClick}
            >
                {<FormattedMessage id="save"/>}
            </Button>
        )}

    </>

    const disabledHoursFrom = () => [0, 1, 2, 3, 4, 5, 6, 18, 7, 19, 20, 21, 22, 23];
    const disabledHoursTo = () => [0, 1, 2, 3, 4, 5, 6, 7, 20, 21, 22, 23];

    const disabledMinutesForTo = (selectedHour: number): number[] => {
        if (selectedHour === 19) {
            return Array.from(Array(59).keys());
        }

        return [];
    };

    const formDesktop = <Form form={form} key={formKey} layout="vertical">
        <Modal
            transitionName=""
            className={'dentobook-modal'}
            open={isOpen}
            title={selectedEvent?.id ? <FormattedMessage id="edit_appointment"/> : <FormattedMessage id="appointmentHour"/>}
            onCancel={onClose}
            footer={footer}
            closeIcon={<CloseOutlined className={'icon-24px'}/>}
        >

            <Row gutter={24}>
                <Col span={24}>
                    <Form.Item
                        name="patient"
                        label={<FormattedMessage id="patient"/>}
                        initialValue={selectedPatient}
                        className={"dentobook-form-item dentobook-centered-select"}
                        rules={[
                            {
                                required: true,
                                message: <FormattedMessage id="required_field"/>,
                            },
                            {
                                validator: (_, value) => {
                                    let stringValue = Array.isArray(value) ? value.join(', ') : value;

                                    if (stringValue && stringValue.length > 150) {
                                        return Promise.reject(new Error(intl.formatMessage({id: 'max_length_150'})));
                                    }
                                    return Promise.resolve();
                                },
                            }

                        ]}
                    >
                        {loading ? (
                            <Spin/>
                        ) : (
                            <div ref={selectRef}>
                                <Select
                                    mode="tags"
                                    size="large"
                                    className="dentobook-input"
                                    allowClear
                                    optionFilterProp="label"
                                    value={selectValue}
                                    suffixIcon={<CaretDownOutlined/>}
                                    onChange={(value, option) => {
                                        if (document.activeElement instanceof HTMLElement) {
                                            document.activeElement.blur();
                                        }

                                        const updatedValue = [...value];

                                        if (updatedValue.length === 0) {
                                            setSelectValue([]);
                                            setSelectedPatient(undefined);
                                            setSelectedUnregisteredPatient('');
                                        } else {
                                            const latestValue = updatedValue[updatedValue.length - 1];
                                            setSelectValue([latestValue]);

                                            const isExistingPatient = patients.some(patient => patient.id === latestValue);
                                            if (isExistingPatient) {
                                                setSelectedPatient(latestValue);

                                                setSelectedUnregisteredPatient('');
                                            } else {
                                                setSelectedUnregisteredPatient(latestValue);

                                                setSelectedPatient(undefined);
                                            }
                                        }

                                    }}
                                    options={options}
                                />
                            </div>
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={16}>
                    <Form.Item
                        label={<FormattedMessage id="MEDICAL_DOCTOR"/>}
                        name="doctor"
                        initialValue={selectedDoctor}
                        className={"dentobook-form-item"}
                        rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Select
                            size={"large"}
                            placeholder="Select a doctor"
                            className="dentobook-input"
                            allowClear
                            value={selectedDoctor}
                            suffixIcon={<CaretDownOutlined/>}
                            onChange={(value) => {
                                setSelectedEvent(prev => prev ? { ...prev, doctor: value } : null);
                                setSelectedDoctor(value);
                                onDoctorChange(value);
                            }}
                            options={doctorOptions}
                        >
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        label={<FormattedMessage id="clinic"/>}
                        name="clinic"
                        initialValue={selectedRoom}
                        className={"dentobook-form-item"}
                        rules={[{required: clinics.length >= 2, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Select
                            size={"large"}
                            className="dentobook-input"
                            value={selectedRoom}
                            suffixIcon={<CaretDownOutlined/>}
                            allowClear
                            onChange={(value) => {
                                setSelectedEvent({...selectedEvent, clinicId: value, notes: selectedNotes});
                                setSelectedRoom(value);
                                onRoomChange(value);
                            }}
                        >
                            {isClinicDeleted && (
                                <Option key={selectedRoom} value={selectedRoom} disabled>
                                    <FormattedMessage id="deleted_clinic"/>
                                </Option>
                            )}
                            {clinics.map((clinicId) => (
                                <Option key={clinicId.id} value={clinicId.id}>
                                    {clinicId.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item
                        label={<FormattedMessage id="date"/>}
                        initialValue={selectedDate}
                        className={"dentobook-form-item"}
                        rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <DatePicker size={"large"} value={selectedDate} onChange={handleDateChange}
                                    style={{width: '100%'}}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item
                        label={<FormattedMessage id="from"/>}
                        initialValue={startValue}
                        className={"dentobook-form-item"}
                        rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <TimePicker size={"large"} value={startValue} onCalendarChange={onStartChange}
                                    style={{width: '100%'}} format="HH:mm"
                                    minuteStep={5}
                        />
                    </Form.Item>
                </Col>

                <Col span={6}>
                    <Form.Item
                        label={<FormattedMessage id="to"/>}
                        initialValue={endValue}
                        className={"dentobook-form-item"}
                        rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                        help={endTimeError}
                        validateStatus={endTimeError ? "error" : ""}
                    >
                        <TimePicker size={"large"} value={endValue} onCalendarChange={onEndChange} style={{width: '100%'}}
                                    format="HH:mm"
                                    minuteStep={5}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col span={24} className="">
                    <Form.Item
                        label={<FormattedMessage id="notes"/>}
                        name="notes"
                        className={"dentobook-form-item"}
                        initialValue={selectedNotes}
                    >
                        <Input.TextArea
                            className="event-modal-textarea"
                            rows={4}
                            placeholder="Notes"
                            value={selectedNotes}
                            onChange={(value) => {
                                onNotesChange(value);
                            }}
                        />
                    </Form.Item>
                </Col>
            </Row>
        </Modal>

    </Form>

    const formMobile = <Form form={form} key={formKey} layout="vertical">
        <Modal
            transitionName=""
            styles={{body: {overflowY: 'auto', maxHeight: 'calc(100vh - 300px)'}}}
            className={'dentobook-modal'}
            open={isOpen}
            title={selectedEvent?.id ? <FormattedMessage id="edit_appointment"/> : <FormattedMessage id="appointmentHour"/>}
            onCancel={onClose}
            closeIcon={<CloseOutlined className={'icon-24px'}/>}
            footer={footer}
        >
            <Row>
                <Col span={24}>
                    <Form.Item
                        name="patient"
                        label={<FormattedMessage id="patient"/>}
                        initialValue={selectedPatient}
                        className={"dentobook-form-item dentobook-centered-select"}
                        rules={[
                            {
                                required: true,
                                message: <FormattedMessage id="required_field"/>,
                            },
                            {
                                validator: (_, value) => {
                                    let stringValue = Array.isArray(value) ? value.join(', ') : value;

                                    if (stringValue && stringValue.length > 150) {
                                        return Promise.reject(new Error(intl.formatMessage({id: 'max_length_150'})));
                                    }
                                    return Promise.resolve();
                                },
                            }

                        ]}
                    >
                        {loading ? (
                            <Spin/>
                        ) : (
                            <div ref={selectRef}>
                                <Select
                                    mode="tags"
                                    size="large"
                                    className="dentobook-input"
                                    allowClear
                                    optionFilterProp="label"
                                    value={selectValue}
                                    suffixIcon={<CaretDownOutlined/>}
                                    onChange={(value, option) => {
                                        if (document.activeElement instanceof HTMLElement) {
                                            document.activeElement.blur();
                                        }
                                        const updatedValue = [...value];

                                        if (updatedValue.length === 0) {
                                            setSelectValue([]);
                                            setSelectedPatient(undefined);
                                            setSelectedUnregisteredPatient('');
                                        } else {
                                            const latestValue = updatedValue[updatedValue.length - 1];
                                            setSelectValue([latestValue]);

                                            const isExistingPatient = patients.some(patient => patient.id === latestValue);
                                            if (isExistingPatient) {
                                                setSelectedPatient(latestValue);

                                                setSelectedUnregisteredPatient('');
                                            } else {
                                                setSelectedUnregisteredPatient(latestValue);

                                                setSelectedPatient(undefined);
                                            }
                                        }

                                    }}
                                    options={options}
                                />
                            </div>
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item
                        label={<FormattedMessage id="MEDICAL_DOCTOR"/>}
                        name="doctor"
                        initialValue={selectedDoctor}
                        className={"dentobook-form-item"}
                        rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Select
                            size={"large"}
                            placeholder="Select a doctor"
                            className="dentobook-input"
                            allowClear
                            value={selectedDoctor}
                            suffixIcon={<CaretDownOutlined/>}
                            onChange={(value) => {
                                setSelectedEvent(prev => prev ? { ...prev, doctor: value } : null);
                                setSelectedDoctor(value);
                                onDoctorChange(value);
                            }}
                            options={doctorOptions}
                        >
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item
                        label={<FormattedMessage id="clinic"/>}
                        name="clinic"
                        initialValue={selectedRoom}
                        className={"dentobook-form-item"}
                        rules={[{required: clinics.length >= 2, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <Select
                            size={"large"}
                            className="dentobook-input"
                            value={selectedRoom}
                            suffixIcon={<CaretDownOutlined/>}
                            allowClear
                            onChange={(value) => {
                                setSelectedEvent({...selectedEvent, clinicId: value, notes: selectedNotes});
                                setSelectedRoom(value);
                                onRoomChange(value);
                            }}
                        >
                            {isClinicDeleted && (
                                <Option key={selectedRoom} value={selectedRoom} disabled>
                                    <FormattedMessage id="deleted_clinic"/>
                                </Option>
                            )}
                            {clinics.map((clinicId) => (
                                <Option key={clinicId.id} value={clinicId.id}>
                                    {clinicId.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <Form.Item
                        label={<FormattedMessage id="date"/>}
                        initialValue={selectedDate}
                        className={"dentobook-form-item"}
                        rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <DatePicker size={"large"} value={selectedDate} onChange={handleDateChange}
                                    style={{width: '100%'}}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={12}>
                    <Form.Item
                        label={<FormattedMessage id="from"/>}
                        initialValue={startValue}
                        className={"dentobook-form-item"}
                        rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <TimePicker size={"large"} value={startValue} onCalendarChange={onStartChange} format="HH:mm"
                                    minuteStep={5}

                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item
                        label={<FormattedMessage id="to"/>}
                        initialValue={endValue}
                        className={"dentobook-form-item"}
                        rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                    >
                        <TimePicker size={"large"} value={endValue} onCalendarChange={onEndChange} format="HH:mm"
                                    minuteStep={5}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={24}>
                <Col span={24} className="">
                    <Form.Item
                        label={<FormattedMessage id="notes"/>}
                        name="notes"
                        className={"dentobook-form-item"}
                        initialValue={selectedNotes}
                    >
                        <Input.TextArea
                            className="event-modal-textarea"
                            rows={4}
                            placeholder="Notes"
                            value={selectedNotes}
                            onChange={(value) => {
                                onNotesChange(value);
                            }}
                        />
                    </Form.Item>
                </Col>
            </Row>
        </Modal>
    </Form>;

    const deleteModal = (
        <Modal
            transitionName=""
            okButtonProps={{
                className: "modal-btn"
            }}
            cancelButtonProps={{
                className: "modal-btn"
            }}
            className="dentobook-modal"
            title={<FormattedMessage id="deleteCalendarEvent"/>}
            okText={intl.formatMessage({id: 'save'})}
            cancelText={intl.formatMessage({id: 'cancel'})}
            open={isDeleteModalVisible}
            onOk={(e) => onDeleteButtonClick(e)}
            onCancel={handleDeleteConditionCancel}
        >
            <p><FormattedMessage id="eventDeleteConfirmation"/></p>
        </Modal>
    );

    return (
        <>
            {isDeleteModalVisible ? deleteModal : ''}
            {isMobileOnly ? formMobile : formDesktop}
        </>
    );
                                                             };
export default EventModalWrapper;